<template>
  <div class="access-filter-dropdown">
    <div class="access-filter-dropdown__header">
      <div class="access-filter-dropdown__search">
        <AppInput
          v-model="search"
          placeholder="Поиск"
          show-search-button
          @click.stop
        />
      </div>

      <div v-if="!singleMode" class="access-filter-dropdown__controls">
        <div class="access-filter-dropdown__controls-left">
          <AppCheckbox
            :value="showOnlySelected"
            @on-input="showOnlySelected = !showOnlySelected"
            @click.stop
          />
          <span>Показать выбранных</span>
        </div>
        <button
          type="button"
          class="access-filter-dropdown__reset"
          :disabled="selectedIds.length === 0"
          @click.stop="updateSelected([])"
        >
          Сбросить
        </button>
      </div>
    </div>

    <div v-if="loading" class="access-filter-dropdown__state">
      <q-spinner color="primary" size="28px" />
    </div>
    <div v-else class="access-filter-dropdown__options">
      <UserGroupDropdownContent
        :items="filteredItems"
        :selected="selectedIds"
        :get-group-state="getGroupState"
        :update-selected="updateSelected"
        :search="search"
        :single-mode="singleMode"
        :current-owner="singleMode ? selectedIds[0] ?? null : null"
        :close="singleMode ? close : undefined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { AccessApi } from '@/ api/AccessApi';
import type { WorkersAndAccessGroupsInSettingsGiver } from '@/models/interfaces/access/entity-rules';
import type { SelectGroup, SelectOption } from '@/components/ui-kit/select/AppSelect.vue';
import UserGroupDropdownContent from '@/components/permitions/UserGroupDropdownContent.vue';
import AppInput from '@/components/ui-kit/input/AppInput.vue';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';

interface EntityFilter {
  Entity: { Id: number; Name: string };
  Roles: any[];
  Positive: boolean;
}

const props = defineProps<{
  modelValue: EntityFilter[];
  singleMode?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: EntityFilter[]): void;
  (e: 'close'): void;
}>();

const loading = ref(false);
const search = ref('');
const showOnlySelected = ref(false);
const source = ref<WorkersAndAccessGroupsInSettingsGiver | null>(null);

const items = computed<SelectOption[]>(() => {
  const result: SelectOption[] = [];
  const withoutDepartment: SelectOption[] = [];

  source.value?.Holders?.forEach((holder, index) => {
    if (holder.Departament) {
      result.push({
        id: index + 999999,
        label: holder.Departament.Name,
        type: 'group',
        selectable: false,
        children: (holder.Departament.Workers ?? []).map((worker) => ({
          id: worker.GroupId,
          label: worker.Name,
          type: 'item',
        })),
      });
    } else if (holder.IsWorker && holder.Worker) {
      withoutDepartment.push({
        id: holder.Worker.GroupId,
        label: holder.Worker.Name,
        type: 'item',
      });
    }
  });

  for (const group of source.value?.Groups ?? []) {
    if (!group.IsPersonal) {
      result.push({
        id: group.Id,
        label: group.Name,
        type: 'group',
        selectable: true,
        children: [],
      });
    }
  }

  if (withoutDepartment.length) {
    result.push({
      id: -1,
      label: 'Без отдела',
      type: 'group',
      selectable: false,
      children: withoutDepartment,
    });
  }
  return result;
});

const filteredItems = computed<SelectOption[]>(() => {
  const term = search.value.trim().toLocaleLowerCase();
  return items.value.flatMap((item) => {
    const labelMatches = item.label.toLocaleLowerCase().includes(term);
    if (!('children' in item) || !item.children?.length) {
      const isSelected = selectedIds.value.includes(item.id);
      return (!showOnlySelected.value || isSelected) && (!term || labelMatches) ? [item] : [];
    }
    let children = !term || labelMatches
      ? item.children
      : item.children.filter((child) => child.label.toLocaleLowerCase().includes(term));
    if (showOnlySelected.value) {
      children = children.filter((child) => selectedIds.value.includes(child.id));
    }
    return children.length ? [{ ...item, children }] : [];
  });
});

const selectedIds = computed<(string | number)[]>(() =>
  (props.modelValue ?? []).map((filter) => filter.Entity.Id),
);

function findOption(id: string | number): SelectOption | undefined {
  for (const item of items.value) {
    if (item.id === id) return item;
    if ('children' in item) {
      const child = item.children?.find((candidate) => candidate.id === id);
      if (child) return child;
    }
  }
  return undefined;
}

function updateSelected(ids: (string | number)[]) {
  const normalizedIds = props.singleMode && ids.length ? [ids[ids.length - 1]] : ids;
  const previous = new Map((props.modelValue ?? []).map((filter) => [filter.Entity.Id, filter]));
  const next = normalizedIds.flatMap((id) => {
    const numericId = Number(id);
    const existing = previous.get(numericId);
    if (existing) return [existing];
    const option = findOption(id);
    if (!option || !Number.isFinite(numericId) || numericId < 0) return [];
    return [{ Entity: { Id: numericId, Name: option.label }, Roles: [], Positive: true }];
  });
  emit('update:modelValue', next);
}

function getGroupState(group: SelectGroup) {
  const childIds = group.children?.map((child) => child.id) ?? [];
  return {
    isSelected: childIds.length > 0 && childIds.every((id) => selectedIds.value.includes(id)),
    isDisabled: childIds.length === 0,
  };
}

function close() { emit('close'); }

onMounted(async () => {
  loading.value = true;
  try {
    source.value = await AccessApi.getWorkersAndAccessGroups();
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
.access-filter-dropdown {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.access-filter-dropdown__header {
  flex: 0 0 auto;
}

.access-filter-dropdown__search {
  width: 430px;
  padding: 12px 16px 8px;
}

.access-filter-dropdown__controls {
  min-height: 41px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #3f3f3f;
  font-size: 14px;
}

.access-filter-dropdown__controls-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.access-filter-dropdown__reset {
  padding: 4px 8px;
  border: none;
  background: none;
  color: #979797;
  font-size: 14px;
  cursor: pointer;
}

.access-filter-dropdown__reset:hover:not(:disabled) {
  color: #3f884f;
  text-decoration: underline;
}

.access-filter-dropdown__reset:disabled {
  color: #d2d2d2;
  cursor: not-allowed;
  text-decoration: none;
}

.access-filter-dropdown__state {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-filter-dropdown__options {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
}

:deep(.grid) {
  display: flex;
  min-height: 100%;
}
</style>
